import React from "react";
import LegalLayout, { LegalSection, LegalList } from "@/components/legal/LegalLayout";
import { Link } from "react-router-dom";
import { UploadCloud, Users, Camera, Archive, CreditCard, LifeBuoy } from "lucide-react";

/**
 * Help / Getting Started — public, no auth.
 *
 * Founder Release Correction (2026-09-08) -- the "internal runbooks +
 * customer-facing help docs" P2 item had a real, confirmed gap:
 * operator runbooks existed but nothing explained the core Starter
 * workflows to an actual customer. Deliberately reuses the SAME
 * LegalLayout + support-contact model as `/support` (this page links
 * out to it for anything not covered here) rather than building a
 * second, separate help/contact system.
 */
export default function Help() {
  return (
    <LegalLayout
      testid="page-help"
      eyebrow="Getting Started"
      title="ForgedOps Plans Help"
      intro="A quick walkthrough of the core Starter workflows -- from your first plan upload to inviting your crew. For anything not covered here, visit Support."
      metaTitle="Help · ForgedOps Plans"
      metaDescription="Getting started with ForgedOps Plans: upload plans, invite your crew, capture field photos, and manage storage."
      canonical="https://forgedopsplans.com/help"
    >
      <LegalSection id="upload-plans" title="1. Upload Your Plan Set">
        <div className="flex items-start gap-2 mb-2">
          <UploadCloud size={16} style={{ color: "var(--fop-primary)" }} className="mt-0.5" />
          <p className="mb-0">From a project, choose <strong>Upload Plans</strong> and select your PDF plan set. Each sheet is automatically detected and split for you to review.</p>
        </div>
        <LegalList items={[
          "Every uploaded sheet needs a quick review before it's published -- confirm the auto-detected sheet number/title, or correct it.",
          "A large plan set (dozens of pages) may take a short while to process -- you can leave the page and come back; nothing is lost.",
          "Publishing only some sheets from a set is normal -- the rest stay in \"needs review\" until you're ready for them.",
        ]} />
      </LegalSection>

      <LegalSection id="invite-crew" title="2. Invite Your Crew">
        <div className="flex items-start gap-2 mb-2">
          <Users size={16} style={{ color: "var(--fop-primary)" }} className="mt-0.5" />
          <p className="mb-0">From Settings &rarr; Team, invite teammates by email and assign a role. Starter includes up to 5 users.</p>
        </div>
        <LegalList items={[
          "Invited teammates get an email with a link to set their password.",
          "You can disable and re-enable a teammate's access at any time without losing their history.",
        ]} />
      </LegalSection>

      <LegalSection id="field-capture" title="3. Capture in the Field">
        <div className="flex items-start gap-2 mb-2">
          <Camera size={16} style={{ color: "var(--fop-primary)" }} className="mt-0.5" />
          <p className="mb-0">Pin photos, RFIs, and punch items directly onto the plan sheet from your phone or tablet in the field.</p>
        </div>
        <LegalList items={[
          "No signal? Keep capturing -- photos and pins queue on your device and sync automatically the moment you're back online.",
          "Never close the app mid-sync on a spotty connection -- let it finish, or it will simply retry next time you're online.",
        ]} />
      </LegalSection>

      <LegalSection id="storage-limits" title="4. Storage & Plan Limits">
        <div className="flex items-start gap-2 mb-2">
          <Archive size={16} style={{ color: "var(--fop-primary)" }} className="mt-0.5" />
          <p className="mb-0">Starter includes 3 active projects, 150 active plan pages, 15 GiB of storage (approximately 16.1 GB), and 100 Ask queries per month, shared across your whole team. Check your live usage anytime in Settings &rarr; Billing.</p>
        </div>
        <LegalList items={[
          "Finished a job? Archive the project -- it frees up your active-project and active-page room for the next one. Nothing is deleted.",
          "Archived projects, their plans, and their photos stay fully counted toward your 15 GiB and stay accessible -- archiving only frees active-project/page room, not storage.",
        ]} />
      </LegalSection>

      <LegalSection id="billing" title="5. Billing & Your Plan">
        <div className="flex items-start gap-2 mb-2">
          <CreditCard size={16} style={{ color: "var(--fop-primary)" }} className="mt-0.5" />
          <p className="mb-0">Manage your subscription, payment method, and invoices anytime from Settings &rarr; Billing -- it opens Stripe's secure Billing Portal.</p>
        </div>
        <LegalList items={[
          "You'll see your next renewal date and amount before any charge.",
          "Need a different plan tier? You can change plans right from the same page.",
        ]} />
      </LegalSection>

      <LegalSection id="more-help" title="Still Need Help?">
        <div className="flex items-start gap-2">
          <LifeBuoy size={16} style={{ color: "var(--fop-primary)" }} className="mt-0.5" />
          <p className="mb-0">
            Visit <Link to="/support" className="underline underline-offset-2" style={{ color: "var(--fop-primary)" }} data-testid="help-support-link">Support</Link> for
            direct contact, response-time expectations, and how to report an issue.
          </p>
        </div>
      </LegalSection>
    </LegalLayout>
  );
}
